import React, { useState } from "react";
import CustomDropDown from "../DropDown";
import { ResponsiveState } from "../../utils/responsiveState";

interface PaginationProps {
  totalItems: number;
  itemsPerPageOptions: number[];
  onPageChange: any;
  onItemsPerPageChange: any;
  pageNo?: number;
  initialItemsPerPage?: number;
  className?: string;
  rightmodalPagination?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  totalItems,
  itemsPerPageOptions,
  onPageChange,
  onItemsPerPageChange,
  rightmodalPagination,
  pageNo,
  className,
  initialItemsPerPage,
}) => {
  const [currentPage, setCurrentPage] = useState(pageNo || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    initialItemsPerPage || itemsPerPageOptions[0]
  );
  const { isLgScreen } = ResponsiveState();

  const totalPages: any = Math.ceil(totalItems / itemsPerPage);
  let pageNoId = 0;
  let numOftabs = 3;
  let lastPageNumber = totalPages - 3;

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    onPageChange({ currentPage: pageNumber, itemsPerPage });
  };

  const setPageNo = (e: any) => {
    setCurrentPage(parseInt(e.target.textContent));
    onPageChange({ currentPage: parseInt(e.target.textContent), itemsPerPage });
    pageNoId = parseInt(e.target.id);
  };

  const handleItemsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setItemsPerPage(Number(event.target.value));
    let temp = Number(event.target.value);
    setCurrentPage(1);
    onItemsPerPageChange({ currentPage, itemsPerPage: temp });
  };

  return (
    <div
      className={`${
        rightmodalPagination
          ? "flex flex-col gap-4"
          : "flex flex-col gap-4 sm:gap-5 lg:grid lg:grid-cols-2 lg:gap-0 justify-end items-center"
      } ${className} mt-6 sm:mt-8 px-4 sm:px-6 mb-12 sm:mb-20`}
    >
      <div className="w-full max-w-full flex items-center gap-2 overflow-x-auto pb-1 pr-3 box-border">
        <button
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
          className="w-9 h-9 sm:w-10 sm:h-10 flex-shrink-0 flex items-center justify-center rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:hover:bg-gray-200 text-sm font-Open font-medium"
        >
          &lt;
        </button>
        {totalPages < 9 ? (
          Array.from(Array(totalPages).keys())?.map((page: any) => {
            return (
              <button
                key={page}
                onClick={() => handlePageChange(page + 1)}
                className={`w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-md text-[12px] sm:text-sm font-Open font-medium transition-colors ${
                  currentPage === page + 1
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                {page + 1}
              </button>
            );
          })
        ) : (
          <>
            {Array.from(Array(numOftabs).keys())?.map((page: any) => {
              return (
                <button
                  key={page}
                  id={page}
                  onClick={(e) => setPageNo(e)}
                  className={`w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-md text-[12px] sm:text-sm font-Open font-medium transition-colors ${
                    currentPage ===
                    (lastPageNumber - currentPage > 4 ? currentPage + page : page + 1)
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 hover:bg-gray-300"
                  }`}
                >
                  {lastPageNumber - currentPage > 4
                    ? currentPage + page
                    : page + 1}
                </button>
              );
            })}
            {Array.from(Array(numOftabs).keys())?.map((_, i) => {
              return (
                <span
                  key={`ellipsis-${i}`}
                  className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center text-[12px] sm:text-sm font-Open font-medium text-[#777777]"
                  aria-hidden="true"
                >
                  …
                </span>
              );
            })}
            {Array.from(Array(numOftabs).keys())?.map((page: any) => {
              lastPageNumber = lastPageNumber + 1;
              return (
                <button
                  onClick={() => handlePageChange(lastPageNumber + (page - 2))}
                  className={`w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-md text-[12px] sm:text-sm font-Open font-medium transition-colors ${
                    currentPage === lastPageNumber
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 hover:bg-gray-300"
                  }`}
                >
                  {lastPageNumber}
                </button>
              );
            })}
          </>
        )}

        <button
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
          className="w-9 h-9 sm:w-10 sm:h-10 flex-shrink-0 flex items-center justify-center rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:hover:bg-gray-200 text-sm font-Open font-medium"
        >
          &gt;
        </button>
      </div>
      <div
        className={`flex flex-col sm:flex-row sm:items-center gap-2 pt-2 ${
          rightmodalPagination ? "" : "lg:justify-end"
        }`}
      >
        <div className="relative z-20 flex items-center justify-between sm:justify-start gap-x-2 sm:mr-10">
          <p className="font-Open font-normal text-sm text-[#323232] whitespace-nowrap">
            Items per page
          </p>
          <CustomDropDown
            options={itemsPerPageOptions?.map((option) => ({
              label: option,
              value: option,
            }))}
            onChange={handleItemsPerPageChange}
            value={itemsPerPage}
            selectClassName="!h-[36px] sm:!h-[38px] min-w-[84px]"
            useNativeSelect={isLgScreen}
          />
        </div>
        <p className="font-Open font-semibold text-sm text-[#A4A4A4] whitespace-nowrap">
          Item{" "}
          {(currentPage - 1) * itemsPerPage === 0
            ? 1
            : (currentPage - 1) * itemsPerPage}{" "}
          to{" "}
          {currentPage * itemsPerPage > totalItems
            ? totalItems
            : currentPage * itemsPerPage}{" "}
          of {totalItems}
        </p>
      </div>
    </div>
  );
};

export default Pagination;
